/**
 * Semântica de eventos: de token cru a significado operacional.
 *
 * Este módulo é a tradução que separa "uma lista de logs" de "uma tela de
 * investigação" (§1). Ele responde, para cada evento, o que a listagem precisa
 * comunicar de relance — que TIPO de atividade é aquela — e a ficha de detalhe
 * precisa rotular — ação, resultado, módulo, integridade.
 *
 * Duas fontes, uma só saída:
 *  - a LISTA só tem `event_type` (a linha não carrega `facets`, de propósito —
 *    §17: nada de payload nem faceta por linha), então a categoria vem dele;
 *  - o DETALHE tem `facets.event_category`/`event_action`, que são a fonte
 *    autoritativa, e caem para `event_type` quando ausentes (§20).
 *
 * Tudo aqui é PURO e defensivo: um token desconhecido é humanizado e cai na
 * categoria neutra, nunca quebra e nunca inventa um veredito (a mesma disciplina
 * dos decodificadores e do painel — não fabricar achado de segurança).
 */

import type { Tone } from '../../components/primitives/Metric.tsx'

/** As famílias de atividade que a tela agrupa e colore. */
export type LogCategory =
  | 'process'
  | 'network'
  | 'dns'
  | 'authentication'
  | 'file'
  | 'registry'
  | 'command'
  | 'other'

export interface CategoryPresentation {
  key: LogCategory
  /** Código curto — o canal PRIMÁRIO de identificação, legível sem cor (§4, §21). */
  code: string
  /** Rótulo por extenso, em pt-BR. */
  label: string
  /**
   * Tom de reforço. Só `accent` e `neutral`: as cores de status (`ok`/`warn`/
   * `crit`) são reservadas para o RESULTADO do evento, e usá-las numa categoria
   * afirmaria um veredito que a categoria não tem.
   */
  tone: Tone
}

const CATEGORY: Record<LogCategory, CategoryPresentation> = {
  process: { key: 'process', code: 'PROC', label: 'Processo', tone: 'neutral' },
  network: { key: 'network', code: 'NET', label: 'Rede', tone: 'accent' },
  dns: { key: 'dns', code: 'DNS', label: 'DNS', tone: 'accent' },
  authentication: { key: 'authentication', code: 'AUTH', label: 'Autenticação', tone: 'accent' },
  file: { key: 'file', code: 'FILE', label: 'Arquivo', tone: 'neutral' },
  registry: { key: 'registry', code: 'REG', label: 'Registro', tone: 'neutral' },
  command: { key: 'command', code: 'CMD', label: 'Comando / script', tone: 'neutral' },
  other: { key: 'other', code: 'EVT', label: 'Evento', tone: 'neutral' },
}

export function categoryPresentation(key: LogCategory): CategoryPresentation {
  return CATEGORY[key]
}

/**
 * Classifica um token normalizado (snake_case) numa família.
 *
 * Serve tanto o `event_type` da linha quanto uma entrada de `event_category` ou
 * o `event_action` do detalhe — todos são o mesmo vocabulário de agente. A ordem
 * dos testes importa: `dns` antes de `network` (é um recorte dele), e a
 * autenticação por palavra-chave porque seus tokens não compartilham prefixo.
 */
export function categoryOf(token: string | null | undefined): LogCategory {
  if (token === null || token === undefined) return 'other'
  const t = token.toLowerCase()

  if (t.startsWith('dns')) return 'dns'
  // `command_execution` e o verbo `amsi_scan` (§12): script inspecionado.
  if (t.startsWith('command') || t.startsWith('amsi')) return 'command'
  if (
    t.startsWith('process') ||
    t === 'image_load' ||
    t === 'create_remote_thread' ||
    t === 'remote_thread' ||
    t === 'pipe_event' ||
    t === 'driver_load'
  ) {
    return 'process'
  }
  if (t.startsWith('network') || t === 'connection' || t.includes('socket')) return 'network'
  if (t.startsWith('registry') || t.startsWith('reg_')) return 'registry'
  if (t.startsWith('file') || t === 'raw_access_read' || t.includes('_file')) return 'file'
  if (
    t.includes('logon') ||
    t.includes('logoff') ||
    t.includes('login') ||
    t.includes('auth') ||
    t.includes('credential') ||
    t.includes('account') ||
    t.includes('session')
  ) {
    return 'authentication'
  }
  if (t === 'iam') return 'authentication'
  return 'other'
}

/**
 * A categoria do detalhe, da fonte autoritativa para a mais fraca (§20):
 * `event_category` primeiro, depois `event_action`, e por fim o `event_type` da
 * linha — que sempre existe.
 */
export function categoryFromFacets(
  eventCategory: readonly string[] | null,
  eventAction: string | null,
  eventType: string,
): LogCategory {
  if (eventCategory !== null) {
    for (const entry of eventCategory) {
      const category = categoryOf(entry)
      if (category !== 'other') return category
    }
  }
  const fromAction = categoryOf(eventAction)
  if (fromAction !== 'other') return fromAction
  return categoryOf(eventType)
}

/** snake_case / kebab → "Sentença capitalizada". Fallback de todo rótulo. */
export function humanize(token: string): string {
  const words = token
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w !== '')
  if (words.length === 0) return token
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ')
}

/**
 * Rótulo de "o que aconteceu", em pt-BR para os verbos comuns e humanizado para
 * o resto. O mapa é curado, não exaustivo: um token fora dele ainda sai legível
 * ("Raw access read"), nunca em branco.
 */
const ACTION_LABEL: Record<string, string> = {
  process_create: 'Processo criado',
  process_creation: 'Processo criado',
  process_start: 'Processo iniciado',
  process_terminate: 'Processo encerrado',
  process_terminated: 'Processo encerrado',
  process_exit: 'Processo encerrado',
  process_access: 'Acesso a processo',
  process_open: 'Acesso a processo',
  process_tampering: 'Adulteração de processo',
  image_load: 'Imagem carregada',
  driver_load: 'Driver carregado',
  create_remote_thread: 'Thread remota criada',
  network_connect: 'Conexão de rede',
  network_connection: 'Conexão de rede',
  network_flow: 'Fluxo de rede',
  dns_query: 'Consulta DNS',
  user_logon: 'Logon de usuário',
  logon: 'Logon de usuário',
  account_logon: 'Logon de usuário',
  user_logoff: 'Logoff de usuário',
  logoff: 'Logoff de usuário',
  file_create: 'Arquivo criado',
  file_created: 'Arquivo criado',
  file_delete: 'Arquivo removido',
  file_deleted: 'Arquivo removido',
  file_modify: 'Arquivo alterado',
  file_rename: 'Arquivo renomeado',
  file_open: 'Arquivo aberto',
  command_execution: 'Execução de comando',
  amsi_scan: 'Script inspecionado (AMSI)',
  raw_access_read: 'Leitura de disco bruto',
  registry_set: 'Registro alterado',
  registry_value_set: 'Registro alterado',
  registry_add: 'Chave de registro criada',
  registry_create: 'Chave de registro criada',
  registry_delete: 'Registro removido',
  registry_rename: 'Registro renomeado',
}

/**
 * Rótulo de ação para a linha e para a ficha. Prefere `event_action` (o verbo
 * fino do detalhe) e cai para o `event_type` da linha.
 */
export function actionLabel(eventType: string, eventAction?: string | null): string {
  const token = eventAction ?? eventType
  return ACTION_LABEL[token.toLowerCase()] ?? humanize(token)
}

/** Resultado do evento: rótulo + tom. É onde as cores de status vivem. */
export interface OutcomePresentation {
  label: string
  tone: Tone
}

export function outcomePresentation(outcome: string | null): OutcomePresentation | null {
  if (outcome === null) return null
  switch (outcome) {
    case 'success':
      return { label: 'Sucesso', tone: 'ok' }
    case 'failure':
      return { label: 'Falha', tone: 'crit' }
    case 'unknown':
      return { label: 'Indeterminado', tone: 'neutral' }
    default:
      // Um valor novo do servidor renderiza como texto, sem virar veredito.
      return { label: humanize(outcome), tone: 'neutral' }
  }
}

/**
 * O vocabulário FECHADO de módulos, na ordem em que o filtro os oferece. O texto
 * é o de fio — vai literal no query param `eventModule` (§5). Um valor fora desta
 * lista é recusado pelo servidor com `logs_event_module_invalid`, então o filtro
 * só oferece o que existe. `unknown` fica de fora: filtrar por "sem módulo" não é
 * uma pergunta de investigação, é ruído.
 */
export const EVENT_MODULES = [
  'sysmon',
  'security',
  'powershell',
  'rdp',
  'task_scheduler',
  'wmi',
  'bits',
  'system',
  'etw',
  'wfp',
  'ebpf',
  'journald',
  'proc',
  'nftables',
] as const

const MODULE_LABEL: Record<string, string> = {
  sysmon: 'Sysmon',
  security: 'Security',
  powershell: 'PowerShell',
  rdp: 'RDP',
  task_scheduler: 'Agendador de Tarefas',
  wmi: 'WMI',
  bits: 'BITS',
  system: 'System',
  // Canais nativos de Windows e a telemetria Linux — sem rótulo curado, cairiam
  // no humanize e sairiam "Etw", "Ebpf", "Wfp"; aqui saem com a grafia correta.
  etw: 'ETW',
  wfp: 'WFP',
  ebpf: 'eBPF',
  journald: 'journald',
  proc: 'proc',
  nftables: 'nftables',
  unknown: 'Desconhecido',
}

export function moduleLabel(module: string | null): string | null {
  if (module === null) return null
  return MODULE_LABEL[module] ?? humanize(module)
}

const INTEGRITY_LABEL: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  system: 'Sistema',
  unknown: 'Desconhecida',
}

/** Integridade do processo: rótulo + tom. `system`/`high` sobem o alerta visual. */
export function integrityPresentation(integrity: string | null): OutcomePresentation | null {
  if (integrity === null) return null
  const label = INTEGRITY_LABEL[integrity] ?? humanize(integrity)
  // Integridade elevada é contexto de risco — um processo em `system` ou `high`
  // é o que o analista repara primeiro. Marca discreta, não veredito.
  const tone: Tone = integrity === 'system' ? 'warn' : 'neutral'
  return { label, tone }
}

const TRANSPORT_LABEL: Record<string, string> = {
  tcp: 'TCP',
  udp: 'UDP',
  icmp: 'ICMP',
  unknown: 'transporte?',
}

export function transportLabel(transport: string | null): string | null {
  if (transport === null) return null
  return TRANSPORT_LABEL[transport] ?? transport.toUpperCase()
}

/** Direção da rede: rótulo por extenso e seta compacta. */
export function directionLabel(direction: string | null): string | null {
  if (direction === null) return null
  switch (direction) {
    case 'inbound':
      return 'Entrada'
    case 'outbound':
      return 'Saída'
    case 'unknown':
      return 'Direção indeterminada'
    default:
      return humanize(direction)
  }
}

const OS_LABEL: Record<string, string> = {
  windows: 'Windows',
  linux: 'Linux',
  mac_os: 'macOS',
  unknown: 'Desconhecido',
}

export function osLabel(os: string | null): string | null {
  if (os === null) return null
  return OS_LABEL[os] ?? humanize(os)
}

const SERVICE_STATE_LABEL: Record<string, string> = {
  running: 'Em execução',
  stopped: 'Parado',
  start_pending: 'Iniciando',
  stop_pending: 'Parando',
  paused: 'Pausado',
  unknown: 'Desconhecido',
}

/**
 * Estado do serviço: rótulo + tom. `stopped` é o que o analista repara num
 * evento de parada de serviço — marca discreta, nunca veredito. Um valor novo do
 * servidor renderiza humanizado, sem tom.
 */
export function serviceStatePresentation(state: string | null): OutcomePresentation | null {
  if (state === null) return null
  const label = SERVICE_STATE_LABEL[state] ?? humanize(state)
  const tone: Tone = state === 'stopped' ? 'warn' : 'neutral'
  return { label, tone }
}

/**
 * O tipo REG_* de um valor de registro, do número de fio (winnt.h). Fora da
 * tabela sai o número, nunca um nome inventado.
 */
const REGISTRY_TYPE: Record<number, string> = {
  0: 'REG_NONE',
  1: 'REG_SZ',
  2: 'REG_EXPAND_SZ',
  3: 'REG_BINARY',
  4: 'REG_DWORD',
  5: 'REG_DWORD_BIG_ENDIAN',
  6: 'REG_LINK',
  7: 'REG_MULTI_SZ',
  8: 'REG_RESOURCE_LIST',
  9: 'REG_FULL_RESOURCE_DESCRIPTOR',
  10: 'REG_RESOURCE_REQUIREMENTS_LIST',
  11: 'REG_QWORD',
}

export function registryTypeLabel(type: number | null): string | null {
  if (type === null) return null
  return REGISTRY_TYPE[type] ?? `tipo ${type}`
}
